import { LoaderCircle, Trash2 } from "lucide-react";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { ApiError } from "../../lib/api.js";
import { deleteAccount } from "./settings-api.js";

const unavailableMessage =
  "Your account was not deleted. You are still signed in; please try again.";

export function DeleteAccountDialog({
  onDeleted,
  onDismiss,
  onSessionInvalid,
}: {
  onDeleted: () => void;
  onDismiss: () => void;
  onSessionInvalid: () => void;
}): React.JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const requestInFlight = useRef(false);
  const headingId = useId();
  const descriptionId = useId();
  const [pending, setPending] = useState(false);
  const [requestError, setRequestError] = useState("");

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const background = [
      ...document.querySelectorAll<HTMLElement>(
        "#main-content, .sidebar, .mobile-app-bar, .sidebar-visibility-control, .settings-topbar, .settings-layout",
      ),
    ];
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    for (const element of background) element.inert = true;
    if (!dialog.open) dialog.showModal();
    cancelRef.current?.focus();
    return () => {
      if (dialog.open) dialog.close();
      document.body.style.overflow = previousOverflow;
      for (const element of background) element.inert = false;
    };
  }, []);

  const dismiss = (): void => {
    if (pending) return;
    setRequestError("");
    onDismiss();
  };

  const confirm = async (): Promise<void> => {
    if (requestInFlight.current) return;
    requestInFlight.current = true;
    setPending(true);
    setRequestError("");
    try {
      await deleteAccount();
      onDeleted();
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        onSessionInvalid();
        return;
      }
      setRequestError(unavailableMessage);
    } finally {
      requestInFlight.current = false;
      setPending(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="change-password-dialog delete-account-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
      onCancel={(event) => {
        event.preventDefault();
        dismiss();
      }}
      onKeyDown={(event) => trapDialogFocus(event, dialogRef.current)}
    >
      <form
        aria-busy={pending}
        onSubmit={(event) => {
          event.preventDefault();
          void confirm();
        }}
      >
        <span className="section-kicker">Delete account</span>
        <h2 id={headingId}>Permanently delete your account?</h2>
        <div id={descriptionId} className="delete-account-warning">
          <strong>This operation is irreversible.</strong>
          <p>
            Your MemoKite account, learning content and history, and uploaded files will be
            permanently deleted from live systems. This cannot be undone.
          </p>
        </div>
        <div className="change-password-message" aria-live="polite">
          {requestError ? (
            <p className="settings-inline-error" role="alert">
              {requestError}
            </p>
          ) : null}
        </div>
        <div className="change-password-actions">
          <button
            ref={cancelRef}
            type="button"
            className="soft-button"
            disabled={pending}
            onClick={dismiss}
          >
            Keep my account
          </button>
          <button type="submit" className="danger-button" disabled={pending}>
            {pending ? (
              <>
                <LoaderCircle className="change-password-spinner" size={16} aria-hidden="true" />
                Deleting account…
              </>
            ) : (
              <>
                <Trash2 size={16} aria-hidden="true" /> Delete my account
              </>
            )}
          </button>
        </div>
      </form>
    </dialog>
  );
}

function trapDialogFocus(
  event: ReactKeyboardEvent<HTMLDialogElement>,
  dialog: HTMLDialogElement | null,
): void {
  if (event.key !== "Tab" || !dialog) return;
  const controls = [
    ...dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ];
  const first = controls[0];
  const last = controls.at(-1);
  if (!first || !last) return;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
